import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton } from "./primitives";

const SPORTS = ["baseball", "softball", "soccer", "basketball", "football", "volleyball", "other"];

/**
 * Create-team sheet — the first thing a coach has to do. Without a team there
 * are no events, no roster, no Chalk Talk. The empty states across the app
 * all funnel here.
 */
export default function CreateTeamSheet({ isOpen, onDismiss, onCreated }) {
  const [name, setName] = useState("");
  const [sport, setSport] = useState("baseball");
  const [teamType, setTeamType] = useState("youth");
  const [seasonLabel, setSeasonLabel] = useState(_defaultSeason());
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim()) {
      toast.error("Give your team a name.");
      return;
    }
    setSaving(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles";
      const data = await api.createTeam({
        name: name.trim(),
        sport,
        team_type: teamType,
        timezone: tz,
        season_label: seasonLabel.trim() || _defaultSeason(),
      });
      toast.success(`${name.trim()} added.`);
      onCreated && onCreated(data.team);
      _reset();
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't create team.");
    } finally {
      setSaving(false);
    }
  }

  function _reset() {
    setName("");
    setSport("baseball");
    setTeamType("youth");
    setSeasonLabel(_defaultSeason());
  }

  return (
    <BottomSheet isOpen={isOpen} title="Create a team" onDismiss={onDismiss}>
      <div data-testid="create-team-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Team name" autoFocus value={name} onChange={setName} placeholder="Reno Riptide" testId="team-name" />
        <Field label="Season" value={seasonLabel} onChange={setSeasonLabel} placeholder="Spring 2026" testId="team-season" />
        <div>
          <div style={_labelStyle}>Team type</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <TypeCard
              active={teamType === "youth"}
              testId="team-type-youth"
              onClick={() => setTeamType("youth")}
              title="Youth team"
              caption="Parents/guardians get invited and manage their kids."
            />
            <TypeCard
              active={teamType === "adult"}
              testId="team-type-adult"
              onClick={() => setTeamType("adult")}
              title="Adult team"
              caption="Players invite themselves directly — no guardians."
            />
          </div>
          <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 6, lineHeight: 1.4 }}>
            Team type is locked once set — pick the one that matches the league.
          </div>
        </div>
        <div>
          <div style={_labelStyle}>Sport</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            {SPORTS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSport(s)}
                data-testid={`team-sport-${s}`}
                style={{
                  padding: "9px 10px", borderRadius: 9,
                  background: sport === s ? "var(--chalk-board)" : "#fff",
                  color: sport === s ? "var(--chalk-white)" : "var(--chalk-board)",
                  border: sport === s ? "1px solid var(--chalk-board)" : "0.5px solid var(--chalk-border)",
                  fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 12,
                  letterSpacing: "0.04em", textTransform: "capitalize", cursor: "pointer",
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <ChalkActionButton
          testId="team-create-submit"
          variant="primary"
          label={saving ? "Creating…" : "Create team"}
          icon={<Plus size={16} aria-hidden="true" />}
          loading={saving}
          onClick={submit}
        />
      </div>
    </BottomSheet>
  );
}

function TypeCard({ active, onClick, title, caption, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        textAlign: "left", padding: "12px 12px", borderRadius: 10,
        background: active ? "var(--chalk-board)" : "#fff",
        color: active ? "var(--chalk-white)" : "var(--chalk-board)",
        border: active ? "1px solid var(--chalk-board)" : "0.5px solid var(--chalk-border)",
        fontFamily: "var(--font-body)", cursor: "pointer",
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 13, letterSpacing: "0.02em" }}>{title}</div>
      <div style={{ marginTop: 4, fontSize: 11, lineHeight: 1.4, opacity: 0.82 }}>{caption}</div>
    </button>
  );
}

function Field({ label, value, onChange, placeholder, testId, autoFocus }) {
  return (
    <label style={{ display: "block" }}>
      <div style={_labelStyle}>{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        style={_inputStyle}
      />
    </label>
  );
}

const _labelStyle = {
  fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
  color: "var(--chalk-muted)", marginBottom: 6,
};
const _inputStyle = {
  width: "100%", border: "0.5px solid var(--chalk-border)",
  borderRadius: 10, padding: "11px 12px", fontSize: 16,
  fontFamily: "inherit", outline: "none",
  background: "#FBF8F2", color: "var(--chalk-board)",
};

function _defaultSeason() {
  const d = new Date();
  const m = d.getMonth();
  const season = m < 3 ? "Winter" : m < 6 ? "Spring" : m < 9 ? "Summer" : "Fall";
  return `${season} ${d.getFullYear()}`;
}
